import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { BACKEND_URL } from "@/constants/api";
import { FreeTrialReportCardProgressGr16202307 } from "@/reports/progress-gr1-6-2023-07/Page";
import { FreeTrialReportCardProgressGr78202307 } from "@/reports/progress-gr7-8-2023-07/Page";
import { FreeTrialReportCardProvincialGr16202309 } from "@/reports/provincial-gr1-6-2023-09/Page";
import { FreeTrialReportCardProvincialGr78202309 } from "@/reports/provincial-gr7-8-2023-09/Page";

const FORM_BY_TYPE = {
  pgrc1to6_2023_07: FreeTrialReportCardProgressGr16202307,
  pgrc7to8_2023_07: FreeTrialReportCardProgressGr78202307,
  pvrc1to6_2023_09: FreeTrialReportCardProvincialGr16202309,
  pvrc7to8_2023_09: FreeTrialReportCardProvincialGr78202309,
};

export const ReportCard = () => {
  const { id } = useParams();
  const { getToken } = useAuth();
  const location = useLocation();

  const [reportType, setReportType] = useState(null);
  const [groupId, setGroupId] = useState(null);
  const [contextInfo, setContextInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/reports/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch report");
        const json = await res.json();
        setReportType(json.reportType);
        setGroupId(json.groupId);

        // Fetch group for grade + schoolYear context
        try {
          const groupRes = await fetch(`${BACKEND_URL}api/report-groups/${json.groupId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (groupRes.ok) {
            const groupJson = await groupRes.json();
            setContextInfo({
              grade: groupJson.grade || '',
              schoolYear: groupJson.schoolYear || '',
              studentName: json.studentName || '',
              oen: json.data?.oen?.trim() || '',
            });
          }
        } catch {
          // context is best-effort; don't fail the whole page
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <div className="container my-6 text-muted-foreground">Loading...</div>;
  if (error) return <div className="container my-6 text-destructive">Error: {error}</div>;

  const FormComponent = FORM_BY_TYPE[reportType];
  if (!FormComponent) {
    return (
      <div className="container my-6 text-muted-foreground">
        Unknown report type: <code>{reportType}</code>
      </div>
    );
  }

  // Determine back URL: shared class or owned class
  const isShared = location.pathname.startsWith("/shared-classes/");
  const backUrl = isShared
    ? location.pathname.replace(/\/reports\/[^/]+$/, "")
    : groupId
    ? `/classes/${groupId}`
    : "/classes";

  return <FormComponent reportId={id} backUrl={backUrl} contextInfo={contextInfo} />;
};
